import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { DashCardEditorDrawer } from "../components/dashboard/DashCardEditorDrawer";
import { DashCardView } from "../components/dashboard/DashCardView";
import { type DashCard, chartColor, newCardId, cardMetrics, metricSpecKey, isMetricItem } from "../lib/dashboard";
import { GRID_COLS, BUILDER_ROW_H, BUILDER_ROW_GAP, resolvePlacement, resolveResize } from "../lib/formLayout";
import "./Items.css";
import "./FormsDashboard.css";
import { startPointerDrag } from "../lib/dragPointer";

/**
 * /formlar giriş kararı: süper-admin → Form Panosu (/formlar/panel), tesis yöneticisi → forms
 * listesi (/formlar/liste). Böylece süper-admin "Formlar" menüsüne bastığında doğrudan pano açılır.
 */
export function FormsIndexRedirect() {
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 60_000 });
  const user = meQ.data?.user;
  if (meQ.isLoading || !user) return null;
  const isSuperAdmin = !!(user as { isSuperAdmin?: boolean }).isSuperAdmin;
  return <Navigate to={isSuperAdmin ? "/formlar/panel" : "/formlar/liste"} replace />;
}

/** Süper-admin çok-formlu analitik panosu — tüm formların KPI özetini kartlar halinde gösterir/düzenler. */
export function FormsDashboard() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const pushToast = useCallback((t: Omit<ToastItem, "id">) => setToasts((p) => [...p.slice(-4), { ...t, id: newCardId() }]), []);
  const dismissToast = useCallback((id: string) => setToasts((p) => p.filter((t) => t.id !== id)), []);

  const layoutQ = trpc.form.dashboard.getLayout.useQuery(undefined, { staleTime: 30_000 });
  const catalogQ = trpc.form.dashboard.fieldsCatalog.useQuery(undefined, { staleTime: 60_000 });

  const [cards, setCards] = useState<DashCard[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<DashCard | null>(null);

  // Sunucu düzenini state'e yükle (kaydedilmemiş değişiklik yokken; kayıt sonrası tazelemede de çalışır).
  const loadedAtRef = useRef<number>(-1);
  useEffect(() => {
    if (layoutQ.data && layoutQ.dataUpdatedAt !== loadedAtRef.current && !dirty) {
      loadedAtRef.current = layoutQ.dataUpdatedAt;
      const layout = (layoutQ.data.layout ?? []) as DashCard[];
      setCards(layout.slice());
    }
  }, [layoutQ.data, layoutQ.dataUpdatedAt, dirty]);

  const catalog = catalogQ.data ?? [];
  const formById = useMemo(() => new Map(catalog.map((c) => [c.formId, c])), [catalog]);
  const fieldMeta = useCallback((formId: string, fieldId: string) => {
    const f = formById.get(formId);
    const fld = f?.numericFields.find((x) => x.id === fieldId);
    return { formName: f?.formName ?? "—", unit: fld?.unit ?? null, label: fld?.label ?? "" };
  }, [formById]);

  const sortedCards = useMemo(() => cards.slice().sort((a, b) => a.y - b.y || a.x - b.x), [cards]);
  // KPI kartının HER metriği için ayrı spec (kart+metrik anahtarı). Grafik kartları hariç (kendi series'i var).
  const specs = useMemo(() => {
    const out: Array<{ key: string; formTemplateId: string; fieldId: string; calc: DashCard["calc"]; from?: string; to?: string }> = [];
    for (const c of cards) {
      if (c.kind !== "kpi") continue;
      for (const m of cardMetrics(c)) {
        if (!isMetricItem(m) || !m.formTemplateId || !m.fieldId || !m.calc) continue; // yalnız form-değeri öğeleri
        out.push({ key: metricSpecKey(c.id, m.id), formTemplateId: m.formTemplateId, fieldId: m.fieldId, calc: m.calc, from: c.from, to: c.to });
      }
    }
    return out;
  }, [cards]);
  const aggQ = trpc.form.dashboard.aggregate.useQuery({ specs }, { enabled: specs.length > 0, staleTime: 15_000 });
  const agg = aggQ.data ?? {};
  // Aggregate hatası (ör. çok fazla metrik) sessiz kalmasın — kullanıcı neden tüm KPI'ların boş olduğunu görsün.
  useEffect(() => { if (aggQ.error) pushToast({ tone: "danger", title: "KPI değerleri yüklenemedi", message: aggQ.error.message }); }, [aggQ.error, pushToast]);

  const saveMut = trpc.form.dashboard.saveLayout.useMutation();
  async function save() {
    try {
      await saveMut.mutateAsync({ layout: cards });
      setDirty(false);
      await utils.form.dashboard.getLayout.invalidate();
      pushToast({ tone: "success", message: "Pano kaydedildi." });
    } catch (e) {
      pushToast({ tone: "danger", title: "Kaydedilemedi", message: (e as Error).message });
    }
  }

  function onEditorSave(card: DashCard) {
    setCards((prev) => {
      const idx = prev.findIndex((c) => c.id === card.id);
      if (idx >= 0) {
        // Düzenleme: yeni boyut (ör. KPI→Grafik'te 6×4) ızgaraya SIĞSIN + komşuyla ÇAKIŞMASIN → çakışmasız slota yerleştir.
        const p = resolvePlacement(asPos(prev), idx, card.x, card.y, card.w, card.h);
        return prev.map((c, i) => (i === idx ? { ...card, x: p.colStart, y: p.rowStart } : c));
      }
      // Yeni kart: tüm kartların ALTINA (y = max(y+h)) → yükseklik hesaba katılır, çakışma olmaz.
      const nextY = prev.reduce((m, c) => Math.max(m, c.y + c.h), 0);
      return [...prev, { ...card, x: 0, y: nextY }];
    });
    setDirty(true);
    setEditorOpen(false);
  }
  function onDelete(id: string) { setCards((prev) => prev.filter((c) => c.id !== id)); setDirty(true); }

  // ── Sürükle-bırak + boyutlandırma (pointer-tabanlı; native DnD DEĞİL — güvenilir + test edilebilir) ──
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  /** İmleç ekran konumundan ızgara hücresini (col,row) hesapla. */
  function cellFromPoint(clientX: number, clientY: number): { col: number; row: number } | null {
    const el = gridRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const colW = (r.width - BUILDER_ROW_GAP * (GRID_COLS - 1)) / GRID_COLS;
    const col = Math.max(0, Math.min(GRID_COLS - 1, Math.floor((clientX - r.left) / (colW + BUILDER_ROW_GAP))));
    const row = Math.max(0, Math.floor((clientY - r.top) / (BUILDER_ROW_H + BUILDER_ROW_GAP)));
    return { col, row };
  }
  const asPos = (cs: DashCard[]) => cs.map((c) => ({ colStart: c.x, rowStart: c.y, colSpan: c.w, rowSpan: c.h }));

  /** Kartı taşı: başlıktan tut → 5px eşik → ızgarada çakışmasız en-yakın slota canlı otur (setDirty sonda). */
  function beginDrag(e: React.MouseEvent, id: string) {
    // Sürükleme çekirdeği lib/dragPointer.ts'te ORTAK (kanban + form builder ile aynı).
    startPointerDrag(e, {
      ignoreSelectors: [".dash-card-acts", ".dash-resize"], // butonlar/boyut kolu drag başlatmaz
      dragClass: "dash-dragging",
      onStart: () => setDragId(id),
      onMove: (x, y) => {
        const cell = cellFromPoint(x, y);
        if (!cell) return;
        setCards((prev) => {
          const idx = prev.findIndex((c) => c.id === id);
          if (idx < 0) return prev;
          const c = prev[idx]!;
          const p = resolvePlacement(asPos(prev), idx, cell.col, cell.row, c.w, c.h);
          return p.colStart === c.x && p.rowStart === c.y ? prev : prev.map((x2, i) => (i === idx ? { ...x2, x: p.colStart, y: p.rowStart } : x2));
        });
      },
      onEnd: (_x, _y, moved) => { if (moved) { setDragId(null); setDirty(true); } },
    });
  }

  /** Kartı boyutlandır: sağ-alt köşe → imleç konumundan w/h (kolon/satır) türet (clamp; setDirty sonda). */
  function beginResize(e: React.MouseEvent, id: string) {
    e.stopPropagation(); e.preventDefault();
    if (e.button !== 0) return;
    document.body.classList.add("dash-resizing");
    let changed = false;
    const move = (ev: MouseEvent) => {
      if (ev.buttons === 0) { up(); return; } // fare pencere dışında bırakıldıysa → temizle
      const el = gridRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const colW = (r.width - BUILDER_ROW_GAP * (GRID_COLS - 1)) / GRID_COLS;
      setCards((prev) => {
        const idx = prev.findIndex((c) => c.id === id); if (idx < 0) return prev;
        const c = prev[idx]!;
        const rightCol = Math.round((ev.clientX - r.left + BUILDER_ROW_GAP) / (colW + BUILDER_ROW_GAP));
        const bottomRow = Math.round((ev.clientY - r.top + BUILDER_ROW_GAP) / (BUILDER_ROW_H + BUILDER_ROW_GAP));
        // Çakışma-farkında: komşuyla çakışan boyut REDDEDİLİR → kart komşuya değene kadar büyür, orada durur.
        const res = resolveResize(asPos(prev), idx, rightCol - c.x, bottomRow - c.y, 20);
        if (!res || (res.w === c.w && res.h === c.h)) return prev;
        changed = true;
        return prev.map((x, i) => (i === idx ? { ...x, w: res.w, h: res.h } : x));
      });
    };
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); document.body.classList.remove("dash-resizing"); if (changed) setDirty(true); };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
  }

  const loading = layoutQ.isLoading || catalogQ.isLoading;

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Form Panosu</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Form Panosu</h1>
          <p className="sub">Tüm formların KPI özeti · kart ekleyip düzenleyin</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {dirty ? <span className="dash-dirty"><Icon name="alertTriangle" size={13} />Kaydedilmemiş</span> : null}
          <button type="button" className="btn-md secondary" onClick={() => navigate("/formlar/liste")}><Icon name="sheet" size={14} />Formlar</button>
          <button type="button" className="btn-md secondary" onClick={() => { setEditingCard(null); setEditorOpen(true); }} disabled={catalog.length === 0}><Icon name="plus" size={14} />Kart Ekle</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={!dirty || saveMut.isPending}><Icon name="check" size={14} />{saveMut.isPending ? "Kaydediliyor…" : "Kaydet"}</button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state" style={{ marginTop: 40 }}><p className="ttl">Yükleniyor…</p></div>
      ) : sortedCards.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 48 }}>
          <Icon name="chart" size={28} />
          <p className="ttl">Henüz kart yok</p>
          <p className="sub">{catalog.length === 0 ? "Önce sayısal alan içeren bir form yayınlayın." : "“Kart Ekle” ile formlardan KPI veya grafik kartı oluşturun."}</p>
        </div>
      ) : (
        <div ref={gridRef} className="dash-grid" style={{ gridAutoRows: `${BUILDER_ROW_H}px`, gap: BUILDER_ROW_GAP, marginTop: 4 }}>
          {sortedCards.map((c, i) => (
            <DashCardView
              key={c.id}
              card={c}
              fieldMeta={fieldMeta}
              color={chartColor(i)}
              agg={agg}
              aggLoading={aggQ.isLoading}
              dragging={dragId === c.id}
              onBeginDrag={(e) => beginDrag(e, c.id)}
              onBeginResize={(e) => beginResize(e, c.id)}
              onEdit={() => { setEditingCard(c); setEditorOpen(true); }}
              onDelete={() => onDelete(c.id)}
            />
          ))}
        </div>
      )}

      <DashCardEditorDrawer open={editorOpen} card={editingCard} catalog={catalog} onClose={() => setEditorOpen(false)} onSave={onEditorSave} />
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
